import React from "react";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import { Skeleton } from "../../components/ui/Skeleton";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import EmptyState from "../../components/ui/EmptyState";
import LetterHeader from "../../components/LetterHeader";
import useAuthStore from "../../stores/useAuthStore";
import { FiPrinter } from "react-icons/fi";

const MainRegisteredCourses = () => {
  const { user: student } = useAuthStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ["registeredCourses"],
    queryFn: async () => {
      const req = await axiosget("ms_course/student");
      if (req.data?.type) {
        throw new Error(req.data.message);
      }
      return req.data;
    },
  });

  const handlePrint = () => window.print();

  const totalCredits = data?.reduce((sum, c) => sum + (c.courseinfo?.credit || 0), 0) || 0;

  return (
    <div>
      {/* Print-only header */}
      <div className="hidden print:block mb-8">
        <LetterHeader />
      </div>

      {/* Screen header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">My Registered Modules</h1>
          <p className="text-sm text-neutral-500 mt-1">Viewing all your registered courses for the current session</p>
        </div>
        <Button onClick={handlePrint} leftIcon={<FiPrinter />}>
          Print Registration
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-12 w-full" />
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
        </div>
      )}

      {error && <Alert variant="danger"><AlertDescription>{error.message || "Failed to load courses"}</AlertDescription></Alert>}

      {data && (
        <>
          {/* Student Info Card */}
          <Card className="mb-6 print:shadow-none print:border-black">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Student Name</p>
                <p className="text-base font-semibold text-neutral-900 mt-1">{student?.firstname} {student?.middlename} {student?.lastname}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Student ID</p>
                <p className="text-base font-semibold text-neutral-900 mt-1">{student?.matricnumber || student?.studentid || student?.username}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Level / Semester</p>
                <p className="text-base font-semibold text-neutral-900 mt-1">{student?.level} / {student?.semester === 1 ? "First" : "Second"}</p>
              </div>
            </div>
          </Card>

          {/* Courses Table */}
          <Card padding="none" className="overflow-hidden print:shadow-none print:border-black">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-primary-600 text-white print:bg-primary-600 print:text-white" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
                  <tr>
                    {["#", "Course Code", "Course Title", "Credit Unit", "Status", "Session", "Level/Semester"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {data.length > 0 ? data.map((course, index) => (
                    <tr key={index} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-4 py-3.5 text-sm font-semibold text-neutral-500">{index + 1}</td>
                      <td className="px-4 py-3.5 text-sm font-bold text-neutral-900">{course.courseinfo?.code}</td>
                      <td className="px-4 py-3.5 text-sm text-neutral-700">{course.courseinfo?.name}</td>
                      <td className="px-4 py-3.5 text-sm text-neutral-700">{course.courseinfo?.credit}</td>
                      <td className="px-4 py-3.5">
                        <Badge variant={course.isfresh ? "success" : "warning"} size="sm">
                          {course.isfresh ? "Fresh" : "Carryover"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-neutral-600">{course.session?.code}</td>
                      <td className="px-4 py-3.5 text-sm text-neutral-700">{course.level} — {course.semester}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="7">
                        <EmptyState variant="no-data" title="No Courses" description="No registered courses found for this session." />
                      </td>
                    </tr>
                  )}
                </tbody>
                {data.length > 0 && (
                  <tfoot className="bg-neutral-50 border-t-2 border-neutral-200">
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-sm font-bold text-neutral-900 text-right">Total Credit Units</td>
                      <td className="px-4 py-3 text-sm font-bold text-primary-600">{totalCredits}</td>
                      <td colSpan="3"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </Card>

          {/* Footer - print only */}
          <div className="mt-12 text-center print:mt-8">
            <p className="font-bold underline mb-2">HOD STAMP & DATE</p>
            <p className="text-xs text-neutral-500 italic">
              Tempering with any score/grade on this document makes it invalid, students are advised to leave this statement of result as is.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default MainRegisteredCourses;
